import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useLogin, useCompanySelect, useLogout } from './formsAuthHooks';
import { useStoreActions } from 'stores/global';
import { useEvent } from 'lib/segment';
import { reloadIntoNewContext } from '../utils/reloadIntoNewContext';
import type { Login, LoginContext } from '../types';

type UseLoginWithNavigationOptions = {
  onSuccessNavigateTo?: string;
  onNoTokenNavigateTo?: string;
  onCompanySelectSuccessNavigateTo?: string;
  onLogoutSuccessNavigateTo?: string;
  onCompanySelectSuccess?: () => void;
  onCompanySelectError?: (error: Error) => void;
  // Set by an in-app company SWITCH, where a previous context's pages are still mounted. The
  // login-time picker leaves this false: nothing stale is mounted, so it keeps the SPA nav.
  reloadOnCompanySelect?: boolean;
};

export const useLoginWithNavigation = (options: UseLoginWithNavigationOptions = {}) => {
  const [searchParams] = useSearchParams();
  const event = useEvent();
  const returnUrl = searchParams.get('returnUrl') || '/';
  const companyReturnUrl =
    returnUrl === '/'
      ? '/switchcompany'
      : `/switchcompany?returnUrl=${encodeURIComponent(returnUrl)}`;
  const {
    onSuccessNavigateTo = returnUrl,
    onNoTokenNavigateTo = companyReturnUrl,
    onCompanySelectSuccessNavigateTo = returnUrl,
    onLogoutSuccessNavigateTo = '/',
    onCompanySelectSuccess,
    onCompanySelectError,
    reloadOnCompanySelect = false,
  } = options;

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { resetPersistedState } = useStoreActions(state => state.urlState);

  const login = useLogin({
    onSuccess: (loginData: Login) => {
      queryClient.clear();
      if (loginData?.token) {
        navigate(onSuccessNavigateTo, { replace: true });
      } else {
        navigate(onNoTokenNavigateTo, { replace: true });
      }
    },
  });

  const companySelect = useCompanySelect({
    onSuccess: () => {
      if (reloadOnCompanySelect) {
        // The Forms cookie now points at the new organization, so the whole session context has
        // moved. Hand it a fresh document; the reload supersedes the cache clear and the
        // in-memory URL-state reset, and CacheIdentityGuard busts the persisted cache on boot.
        onCompanySelectSuccess?.();
        reloadIntoNewContext(onCompanySelectSuccessNavigateTo);
        return;
      }
      queryClient.clear();
      navigate(onCompanySelectSuccessNavigateTo, { replace: true });
      resetPersistedState();
      onCompanySelectSuccess?.();
    },
    onError: onCompanySelectError,
  });

  const logout = useLogout({
    onSuccess: () => navigate(onLogoutSuccessNavigateTo, { replace: true }),
  });

  const submit = (user: string, password: string, RememberMe: boolean) => {
    return Promise.resolve(login.login({ Username: user, Password: password, RememberMe })).catch(
      () => {
        /* surfaced via useLogin's onError notification, catch so we don't send the error to sentry */
      }
    );
  };

  const selectCompany = (loginContext: LoginContext, userData?: Login, length?: number) => {
    if (loginContext?.organizationType === 2) {
      event('Supplier Portal Login Context Selected', {
        objectType: 'Login Context',
        action: 'Selection',
        targetType: 'Organization Context',
        targetId: loginContext.organizationId,
        targetName: loginContext.organizationName,
        source: 'Login Selection',
        page: 'Multi-Select Login',
        businessContext: {
          userId: loginContext.id,
          loginContextType: 'supplier_portal',
          multipleContextsAvailable: length ? length > 1 : false,
        },
      });
    }
    // Return the promise so callers can await completion (e.g. to keep a switch trigger
    // disabled until the request settles). The rejection is swallowed here so callers stay
    // fire-and-forget — failures are surfaced via the mutation's `onError` (onCompanySelectError),
    // not by throwing at the call site (matching the previous `.mutate` behaviour).
    return companySelect
      .mutateAsync({
        loginContext,
        Username: userData?.Username,
        Password: userData?.Password,
        user: userData?.user,
        RememberMe: userData?.RememberMe || false,
      })
      .catch(() => {
        /* surfaced via onCompanySelectError */
      });
  };

  const handleLogout = () => {
    return Promise.resolve(logout()).catch(() => {
      /* logout failures are not surfaced to the user */
    });
  };

  return {
    login,
    companySelect,
    logout,
    submit,
    selectCompany,
    handleLogout,
  };
};
